/**
 * 원격지원 파일 주고받기 — 담당자 원격 창 쪽(RemoteViewerWindow)
 *
 *  - useRemoteFileOperator: RsPeer가 넘겨준 'fx' 채널에 엔진을 붙이고 보내기·가져오기를 연다
 *  - RemoteFilePanel: PC 정보 aside와 같은 모양의 오른쪽 패널(진행률·취소·폴더 열기/다시 저장)
 *
 * 받는 쪽 저장: PC 앱 = Rust → 다운로드 폴더(이 창은 capability가 없지만 앱 커맨드는 부를 수 있다),
 *              웹 = Blob → 브라우저 다운로드. 담당자는 자기가 연 요청(가져오기)에 대한 파일만 받는다.
 * 구버전 직원 앱은 'fx'를 만들지 않는다 → 입력 채널이 열렸는데 'fx'가 안 오면 '업데이트 필요'로 버튼을 끈다.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, FolderOpen, Loader2, Upload, X } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { FxSnapshot, RsFileTransfer } from '../../services/remoteFileTransfer';
import { openReceiveSink, releaseReceivedBlobs, revealReceivedFile } from '../../services/remoteFileSinks';
import { FX_MAX_FILES, checkOutgoing, etaSeconds, fxReasonLabel, percentOf, summarizeFiles } from '../../utils/remoteFileProtocol';
import { formatFileSize } from '../../utils/formatFileSize';
import { formatDuration } from '../../utils/remoteSupportLabels';
import { isTauri } from '../../utils/tauri';

/** ready = 쓸 수 있음 / off = 관리자가 막음 / old_host = 직원 PC 앱이 구버전 / connecting = 연결 중 */
export type FileAvail = 'ready' | 'off' | 'old_host' | 'connecting';

/** 입력 채널이 열린 뒤 이만큼 기다려도 'fx'가 안 오면 구버전 직원 앱으로 본다 */
const OLD_HOST_GRACE_MS = 3000;

export function fileAvailHint(avail: FileAvail): string {
  switch (avail) {
    case 'off':
      return '관리자가 파일 주고받기를 막았습니다';
    case 'old_host':
      return '직원 PC 앱을 업데이트해야 파일을 주고받을 수 있습니다';
    case 'connecting':
      return '직원 PC와 연결하는 중입니다';
    default:
      return '직원 PC와 파일 주고받기';
  }
}

export function useRemoteFileOperator(
  channel: RTCDataChannel | null,
  opts: { enabled: boolean; viewing: boolean; inputChannelsOpen: boolean }
) {
  const { showToast } = useToast();
  const [snap, setSnap] = useState<FxSnapshot | null>(null);
  const [oldHost, setOldHost] = useState(false);
  const engineRef = useRef<RsFileTransfer | null>(null);

  useEffect(() => {
    if (!channel) return;
    const engine = new RsFileTransfer({
      role: 'operator',
      channel,
      openSink: (file) => openReceiveSink(file, null),
      onChange: setSnap,
      onBatchEnd: (b) => {
        if (b.status !== 'done') return;
        const names = summarizeFiles(b.files.map((f) => ({ name: f.savedAs || f.name })));
        showToast({
          type: 'success',
          message:
            b.direction === 'in'
              ? `${isTauri() ? '다운로드 폴더에 저장했습니다' : '받은 파일을 저장했습니다'} — ${names}`
              : `직원 PC 다운로드 폴더에 저장됐습니다 — ${names}`,
        });
      },
    });
    engineRef.current = engine;
    setSnap(engine.snapshot());
    return () => {
      engine.dispose('disconnected');
      engineRef.current = null;
      setSnap(null);
    };
  }, [channel, showToast]);

  // 웹: 받은 파일의 blob URL은 창을 닫을 때 해제한다
  useEffect(() => () => releaseReceivedBlobs(), []);

  // 관리자가 끄면 진행 중인 것을 멈춘다(직원 PC도 같은 정책으로 멈춘다)
  useEffect(() => {
    if (!opts.enabled) engineRef.current?.cancelAll('policy_off');
  }, [opts.enabled]);

  useEffect(() => {
    if (!opts.viewing || !opts.inputChannelsOpen || channel) {
      setOldHost(false);
      return;
    }
    const t = window.setTimeout(() => setOldHost(true), OLD_HOST_GRACE_MS);
    return () => window.clearTimeout(t);
  }, [opts.viewing, opts.inputChannelsOpen, channel]);

  const avail: FileAvail = !opts.enabled
    ? 'off'
    : channel && snap?.open
      ? 'ready'
      : oldHost
        ? 'old_host'
        : 'connecting';

  const sendFiles = useCallback(
    (files: File[]) => {
      const e = engineRef.current;
      if (!e || files.length === 0) return;
      const check = checkOutgoing(files);
      if (check.ok === false) {
        showToast({
          type: 'error',
          message:
            check.reason === 'too_many'
              ? `한 번에 ${FX_MAX_FILES}개까지 보낼 수 있습니다.`
              : check.reason === 'too_large'
                ? '2GB가 넘는 파일은 보낼 수 없습니다.'
                : '보낼 수 없는 파일입니다.',
        });
        return;
      }
      const r = e.sendFiles(files);
      if (r === 'busy') showToast({ type: 'info', message: '파일을 보내는 중입니다. 끝난 뒤 다시 보내 주세요.' });
      else if (r !== true) showToast({ type: 'error', message: '파일을 보내지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    },
    [showToast]
  );

  const requestPull = useCallback(() => {
    const r = engineRef.current?.requestPull();
    if (r === 'busy') showToast({ type: 'info', message: '파일을 받는 중이거나 직원의 응답을 기다리는 중입니다.' });
    else if (r === true) showToast({ type: 'info', message: '직원에게 파일을 요청했습니다. 직원이 보낼 파일을 고르면 받습니다.' });
    else showToast({ type: 'error', message: '파일을 요청하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
  }, [showToast]);

  const cancel = useCallback((bid: number) => engineRef.current?.cancel(bid), []);
  const cancelPull = useCallback(() => engineRef.current?.cancelPull(), []);
  const dismiss = useCallback((bid: number) => engineRef.current?.dismiss(bid), []);

  return { snap, avail, sendFiles, requestPull, cancel, cancelPull, dismiss };
}

interface PanelProps {
  snap: FxSnapshot | null;
  avail: FileAvail;
  hostName: string;
  onClose: () => void;
  onCancel: (bid: number) => void;
  onCancelPull: () => void;
  onDismiss: (bid: number) => void;
}

export const RemoteFilePanel: React.FC<PanelProps> = ({ snap, avail, hostName, onClose, onCancel, onCancelPull, onDismiss }) => {
  const { showToast } = useToast();
  const batches = [...(snap?.batches ?? [])].reverse();

  const reveal = (revealId?: string) => {
    if (!revealId) return;
    revealReceivedFile(revealId).catch(() =>
      showToast({
        type: 'error',
        message: isTauri() ? '폴더를 열지 못했습니다. 다운로드 폴더에서 직접 찾아 주세요.' : '다시 저장하지 못했습니다.',
      })
    );
  };

  return (
    <aside className="w-80 shrink-0 overflow-y-auto bg-slate-800 border-l border-slate-700 p-4 text-sm text-slate-200">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-semibold text-slate-400">파일</h3>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-white/10"
          aria-label="파일 패널 닫기"
        >
          <X size={14} />
        </button>
      </div>

      {avail !== 'ready' && <p className="mb-3 text-xs text-slate-400">{fileAvailHint(avail)}</p>}

      {snap?.pendingPull && (
        <div className="mb-2 rounded-lg bg-slate-700/60 p-3">
          <p className="flex items-center gap-2 text-sm">
            <Loader2 size={14} className="animate-spin text-slate-400 shrink-0" aria-hidden="true" />
            {hostName}님이 보낼 파일을 고르는 중
          </p>
          <button type="button" onClick={onCancelPull} className="mt-2 text-xs text-slate-300 underline hover:text-white">
            요청 취소
          </button>
        </div>
      )}

      {batches.length === 0 && !snap?.pendingPull ? (
        <div className="text-xs text-slate-400 space-y-1.5">
          <p>주고받은 파일이 없습니다</p>
          <p>위 [파일] 버튼에서 직원 PC로 보내거나 직원 PC의 파일을 가져옵니다. 파일마다 직원이 직접 허용합니다.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {batches.map((b) => {
            const isIn = b.direction === 'in';
            const ended = b.status !== 'active' && b.status !== 'waiting';
            const pct = percentOf(b.doneBytes, b.totalBytes);
            const eta = etaSeconds(b.totalBytes - b.doneBytes, b.rate);
            const names = summarizeFiles(b.files.map((f) => ({ name: f.savedAs || f.name })));
            const revealId = b.files.find((f) => f.revealId)?.revealId;
            let status = '';
            if (b.status === 'waiting') status = isIn ? '받을 준비 중' : `${hostName}님이 받기를 고르는 중`;
            else if (b.status === 'active')
              status = `${pct}% · ${formatFileSize(b.doneBytes, { compact: true })} / ${formatFileSize(b.totalBytes, { compact: true })}${
                b.rate > 0 ? ` · ${formatFileSize(Math.round(b.rate), { compact: true })}/s` : ''
              }${eta != null ? ` · ${formatDuration(eta)} 남음` : ''}`;
            else if (b.status === 'done')
              status = isIn
                ? isTauri()
                  ? '다운로드 폴더에 저장했습니다'
                  : '브라우저로 저장했습니다'
                : '직원 PC 다운로드 폴더에 저장됐습니다';
            else if (b.files.length === 0) status = `파일 요청 — ${fxReasonLabel(b.reason)}`;
            else status = fxReasonLabel(b.reason);
            return (
              <li key={`${b.direction}-${b.bid}`} className="rounded-lg bg-slate-700/60 p-3">
                <div className="flex items-start gap-2">
                  {isIn ? (
                    <Download size={14} className="mt-0.5 text-slate-400 shrink-0" aria-hidden="true" />
                  ) : (
                    <Upload size={14} className="mt-0.5 text-slate-400 shrink-0" aria-hidden="true" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-white" title={b.files.map((f) => f.savedAs || f.name).join('\n')}>
                      {names || (isIn ? '받는 파일' : '보내는 파일')}
                    </p>
                    <p className="text-xs text-slate-400 tabular-nums break-anywhere">{status}</p>
                  </div>
                  {ended ? (
                    <button
                      type="button"
                      onClick={() => onDismiss(b.bid)}
                      className="shrink-0 p-1 rounded-md text-slate-400 hover:text-white hover:bg-white/10"
                      aria-label="목록에서 지우기"
                    >
                      <X size={12} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onCancel(b.bid)}
                      className="shrink-0 px-2 py-0.5 rounded-md border border-slate-500 text-xs hover:bg-white/10"
                    >
                      취소
                    </button>
                  )}
                </div>
                {b.status === 'active' && (
                  <div className="mt-2 h-1.5 rounded-full bg-slate-600 overflow-hidden" aria-hidden="true">
                    <div className="h-full bg-brand-500 transition-[width] duration-200" style={{ width: `${pct}%` }} />
                  </div>
                )}
                {isIn && b.status === 'done' && revealId && (
                  <button
                    type="button"
                    onClick={() => reveal(revealId)}
                    className="mt-2 flex items-center gap-1.5 text-xs text-slate-200 hover:text-white"
                  >
                    <FolderOpen size={12} aria-hidden="true" />
                    {isTauri() ? '폴더 열기' : '다시 저장'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <p className="mt-4 text-xs text-slate-500">
        파일은 서버를 거치지 않고 직원 PC와 바로 주고받습니다. 한 번에 {FX_MAX_FILES}개, 파일당 2GB까지.
      </p>
    </aside>
  );
};
